const DEFAULT_ICE = [{ urls: 'stun:stun.l.google.com:19302' }]

let iceCache = null
let iceCacheAt = 0
const ICE_TTL_MS = 5 * 60 * 1000

function parsePayload(payload) {
  if (!payload) return null
  if (typeof payload === 'string') {
    try { return JSON.parse(payload) } catch (e) { return null }
  }
  return payload
}

export async function fetchIceServers() {
  const now = Date.now()
  if (iceCache && now - iceCacheAt < ICE_TTL_MS) {
    return iceCache
  }
  try {
    const { getIceServers } = await import('@/api/mission')
    const { data } = await getIceServers()
    const list = data?.data?.iceServers
    if (Array.isArray(list) && list.length) {
      iceCache = list.map((s) => ({
        urls: s.urls,
        username: s.username || undefined,
        credential: s.credential || undefined,
      }))
      iceCacheAt = now
      return iceCache
    }
  } catch (e) {
    // fall through
  }
  return DEFAULT_ICE
}

export class OBSWebRTCSession {
  constructor({ nodeId, sessionId, deviceName, sendSignal, onState, iceServers }) {
    this.nodeId = nodeId
    this.sessionId = sessionId
    this.deviceName = deviceName || 'OBS Virtual Camera'
    this.sendSignal = sendSignal
    this.onState = onState || (() => {})
    this.iceServers = iceServers || DEFAULT_ICE
    this.pc = null
    this.stopped = false
  }

  async start() {
    this.stopped = false
    this.onState('connecting')
    this.pc = new RTCPeerConnection({ iceServers: this.iceServers })
    this.pc.ontrack = (ev) => {
      const stream = ev.streams && ev.streams[0]
      if (stream) this.onState('live', { stream })
    }
    this.pc.onconnectionstatechange = () => {
      const state = this.pc && this.pc.connectionState
      if (state === 'connected') this.onState('live')
      if (state === 'failed' || state === 'disconnected') this.onState('error', { message: state })
    }
    this.pc.onicecandidate = (ev) => {
      if (!ev.candidate) return
      this.sendSignal({
        action: 'ice',
        payload: ev.candidate.toJSON(),
      })
    }
    this.sendSignal({
      action: 'subscribe',
      payload: { deviceName: this.deviceName },
    })
  }

  async handleHubMessage(msg) {
    if (!msg || msg.type !== 'webrtc') return
    if (msg.correlationId !== this.sessionId) return
    if (msg.nodeId && msg.nodeId !== this.nodeId) return

    const action = msg.action
    const payload = parsePayload(msg.payload)

    if (action === 'error') {
      this.onState('error', { message: (payload && payload.message) || 'Stream error' })
      return
    }
    if (action === 'offer' && payload && payload.sdp) {
      await this.handleOffer(payload.sdp)
      return
    }
    if (action === 'ice' && payload) {
      await this.addIce(payload)
    }
  }

  async handleOffer(sdp) {
    if (!this.pc || this.stopped) return
    await this.pc.setRemoteDescription({ type: 'offer', sdp })
    const answer = await this.pc.createAnswer()
    await this.pc.setLocalDescription(answer)
    this.sendSignal({
      action: 'answer',
      payload: { sdp: answer.sdp },
    })
  }

  async addIce(candidate) {
    if (!this.pc || this.stopped) return
    try {
      await this.pc.addIceCandidate(candidate)
    } catch (e) {
      // ignore late candidates
    }
  }

  stop() {
    this.stopped = true
    if (this.pc) {
      this.pc.ontrack = null
      this.pc.onicecandidate = null
      this.pc.onconnectionstatechange = null
      this.pc.close()
      this.pc = null
    }
    this.sendSignal({ action: 'unsubscribe', payload: {} })
    this.onState('idle')
  }
}

export function newSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `obs-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}
